import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";
import { REVIEW_STATS, formatReviewCount } from "@/data/review-stats";

const AWARDS = [
  {
    org: "Яндекс Карты",
    badge: "Хорошее место 2026",
    detail: `Рейтинг 5,0 · ${formatReviewCount(REVIEW_STATS.yandex.reviewCount)} отзывов`,
    year: "2026",
    href: REVIEW_STATS.yandex.url,
    icon: "★",
  },
  {
    org: "2ГИС",
    badge: "Топ ресторанов СПб",
    detail: `Рейтинг 4,9 · ${formatReviewCount(REVIEW_STATS.twoGis.reviewCount)} отзывов · ${formatReviewCount(REVIEW_STATS.twoGis.ratingCount)} оценок`,
    year: "2025–2026",
    href: REVIEW_STATS.twoGis.url,
    icon: "✦",
  },
  {
    org: "Time Out Санкт-Петербург",
    badge: "Лучший ресторан с видом на город",
    detail: "Ежегодный рейтинг лучших мест города",
    year: "2024",
    href: "https://www.timeout.ru/spb/restaurants",
    icon: "◈",
  },
  {
    org: "The Village Петербург",
    badge: "В гиде «Куда пойти в СПб»",
    detail: "Раздел «Рестораны с панорамным видом»",
    year: "2024",
    href: "https://www.the-village.ru/food/restaurants",
    icon: "◈",
  },
];

const PRESS = [
  {
    outlet: "Time Out Санкт-Петербург",
    category: "Обзор",
    headline: "«Паруса на крыше» — место, где Петербург открывается с лучшей стороны",
    excerpt: "Ресторан на шестом этаже бизнес-центра Olympic Plaza держит планку уже пятнадцать лет. Панорамные окна, открытая терраса и кухня, которая не уступает виду — именно за этим сюда возвращаются снова.",
    href: "https://www.timeout.ru/spb/restaurants",
    year: "2024",
  },
  {
    outlet: "The Village Петербург",
    category: "Гид",
    headline: "Рестораны с видом на Петербург: где поесть и посмотреть на город",
    excerpt: "В нашем гиде по лучшим панорамным ресторанам города «Паруса на крыше» — безусловный лидер. Вид 300°, работает с 2009 года, рейтинг выше пяти — такое сочетание встречается редко.",
    href: "https://www.the-village.ru/food/restaurants",
    year: "2024",
  },
  {
    outlet: "Яндекс Карты",
    category: "Награда",
    headline: "«Хорошее место 2026» — официальная награда Яндекс Карт",
    excerpt: "Статус «Хорошее место» присваивается организациям с высоким рейтингом и большим числом позитивных отзывов. «Паруса на крыше» удерживают рейтинг 5,0 на протяжении нескольких лет подряд.",
    href: REVIEW_STATS.yandex.url,
    year: "2026",
  },
  {
    outlet: "2ГИС",
    category: "Рейтинг",
    headline: "Топ ресторанов Санкт-Петербурга по версии 2ГИС",
    excerpt: `По данным сервиса 2ГИС, «Паруса на крыше» входят в число заведений с наивысшим рейтингом среди ресторанов центра Петербурга. Рейтинг 4,9 основан на ${formatReviewCount(REVIEW_STATS.twoGis.ratingCount)} оценках; гости оставили ${formatReviewCount(REVIEW_STATS.twoGis.reviewCount)} отзывов.`,
    href: REVIEW_STATS.twoGis.url,
    year: "2025–2026",
  },
];

const MEDIA_STRIP = [
  "Time Out",
  "The Village",
  "Яндекс",
  "2ГИС",
  "Afisha Daily",
  "РБК Петербург",
];

export default function Pressa() {
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Пресса и награды — Паруса на крыше | Отзывы СМИ и рейтинги",
    description: "«Паруса на крыше» в прессе: Time Out, The Village, Яндекс «Хорошее место 2026», 2ГИС топ Петербурга. Читайте что пишут о нас.",
    keywords: "Паруса на крыше пресса, ресторан СПб награды, Time Out лучший ресторан Петербург, Хорошее место 2026, рейтинг рестораны СПб",
    canonical: "/pressa",
  });
  useBreadcrumbs([{ name: "Пресса и награды", path: "/pressa" }]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <div className="w-full bg-background">

      {/* Hero */}
      <section className="pt-36 pb-24 border-b border-primary/10 relative overflow-hidden">
        <div
          className="absolute right-0 top-0 font-serif font-light leading-none select-none pointer-events-none"
          style={{ fontSize: "28vw", color: "hsl(166 100% 32% / 0.03)", lineHeight: 1 }}
          aria-hidden="true"
        >
          ◈
        </div>
        <div
          className="container mx-auto px-6 md:px-14 max-w-6xl relative z-10"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(20px)", transition: "all 0.8s ease 0.2s" }}
        >
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Признание</div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-8 leading-tight">
            Пресса<br /><span className="text-accent italic">и награды</span>
          </h1>
          <p className="text-muted-foreground font-light text-lg max-w-xl leading-relaxed">
            С 2009 года «Паруса на крыше» — одно из самых цитируемых и рекомендуемых мест Санкт-Петербурга.
          </p>

          {/* Media strip */}
          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 items-center">
            <span className="text-[9px] tracking-[0.3em] text-primary/30 uppercase">Упоминания в</span>
            {MEDIA_STRIP.map((m) => (
              <span key={m} className="font-serif text-xl font-light text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors cursor-default">
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-12">Награды и рейтинги</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {AWARDS.map((award, i) => (
              <a
                key={i}
                href={award.href}
                target="_blank"
                rel="noreferrer"
                className="group border border-primary/15 hover:border-primary/50 p-8 transition-colors"
                style={{ opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(16px)", transition: `all 0.6s ease ${0.1 + i * 0.08}s` }}
              >
                <div className="text-3xl text-primary/30 group-hover:text-primary/60 transition-colors mb-6 font-serif">{award.icon}</div>
                <div className="text-[8px] tracking-[0.3em] uppercase text-primary/40 mb-2">{award.org}</div>
                <div className="font-serif text-lg font-light text-foreground mb-3 leading-snug group-hover:text-accent transition-colors">{award.badge}</div>
                <div className="text-xs text-muted-foreground/60 font-light leading-relaxed mb-4">{award.detail}</div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-primary/30">{award.year}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Press cards */}
      <section className="py-20 bg-card border-t border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-12">Публикации</div>
          <div className="space-y-5">
            {PRESS.map((item, i) => (
              <a
                key={i}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="group flex flex-col md:flex-row md:items-start gap-6 border border-primary/15 hover:border-primary/40 p-8 md:p-10 transition-colors"
                style={{ opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(12px)", transition: `all 0.6s ease ${0.1 + i * 0.07}s` }}
              >
                <div className="shrink-0 md:w-48">
                  <div className="text-[9px] tracking-[0.25em] uppercase text-primary/40 mb-2">{item.outlet}</div>
                  <div className="inline-block bg-primary/10 border border-primary/20 text-accent text-[8px] tracking-[0.2em] uppercase px-3 py-1.5">{item.category}</div>
                  <div className="text-[9px] tracking-[0.15em] uppercase text-primary/30 mt-3">{item.year}</div>
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-xl md:text-2xl font-light text-foreground mb-3 leading-snug group-hover:text-accent transition-colors">
                    {item.headline}
                  </h3>
                  <p className="text-sm text-muted-foreground font-light leading-relaxed">{item.excerpt}</p>
                </div>
                <div className="shrink-0 self-center hidden lg:block">
                  <span className="text-[9px] tracking-[0.25em] uppercase text-primary/40 group-hover:text-accent transition-colors">Читать →</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Ratings block */}
      <section className="py-20 bg-background border-t border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center gap-3 py-10 border border-primary/10">
              <div className="font-serif text-6xl font-light text-primary">5,0</div>
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => <span key={i} className="text-yellow-400 text-lg">★</span>)}
              </div>
              <div className="text-muted-foreground text-xs tracking-wide">{formatReviewCount(REVIEW_STATS.yandex.reviewCount)} отзывов</div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-primary/40">Яндекс Карты</div>
              <a href={REVIEW_STATS.yandex.url} target="_blank" rel="noreferrer"
                className="text-[9px] tracking-[0.2em] uppercase text-primary/50 hover:text-accent transition-colors mt-1">
                Все отзывы →
              </a>
            </div>
            <div className="flex flex-col items-center gap-3 py-10 border border-primary/10">
              <div className="font-serif text-6xl font-light text-primary">4,9</div>
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => <span key={i} className="text-yellow-400 text-lg">★</span>)}
              </div>
              <div className="text-muted-foreground text-xs tracking-wide">{formatReviewCount(REVIEW_STATS.twoGis.reviewCount)} отзывов · {formatReviewCount(REVIEW_STATS.twoGis.ratingCount)} оценок</div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-primary/40">2ГИС</div>
              <a href={REVIEW_STATS.twoGis.url} target="_blank" rel="noreferrer"
                className="text-[9px] tracking-[0.2em] uppercase text-primary/50 hover:text-accent transition-colors mt-1">
                Открыть в 2ГИС →
              </a>
            </div>
            <div className="flex flex-col items-center gap-3 py-10 border border-primary/10">
              <div className="font-serif text-3xl font-light text-foreground leading-snug text-center">Хорошее<br />место 2026</div>
              <div className="w-8 h-[1px] bg-primary/30 my-1" />
              <div className="text-muted-foreground text-xs tracking-wide text-center">Официальная награда Яндекс Карт</div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-primary/40 text-center">15 лет на рынке</div>
              <Link href="/otzyvy"
                className="text-[9px] tracking-[0.2em] uppercase text-primary/50 hover:text-accent transition-colors mt-1">
                Читать отзывы →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-card border-t border-primary/10">
        <div className="container mx-auto px-6 text-center max-w-xl">
          <div className="w-12 h-[1px] bg-primary/30 mx-auto mb-8" />
          <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-5">
            Убедитесь<br /><span className="text-accent italic">лично</span>
          </h2>
          <p className="text-muted-foreground font-light text-sm leading-relaxed mb-10 max-w-sm mx-auto">
            Приходите и сами решите, заслужены ли эти оценки. Мы уверены в ответе.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={openBookingModal}
              className="bg-primary/20 border border-primary text-accent px-12 py-5 text-[10px] tracking-[0.35em] uppercase hover:bg-primary hover:text-background transition-colors">
              Забронировать стол
            </button>
            <Link href="/otzyvy"
              className="border border-primary/30 text-muted-foreground px-12 py-5 text-[10px] tracking-[0.35em] uppercase hover:border-primary hover:text-accent transition-colors">
              Отзывы гостей →
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
